/**
 * The settings sidebar, one definition shared by the frame and the phone list
 * (docs/frontend-workspace-settings-proposal.md §3 D-1, ruling W-2).
 *
 * `moved: true`   - the section has a page here.
 * `moved: false`  - it has no page yet: it still lives in an old route (the entry
 *                   takes you THERE rather than to a dead page), or it is coming
 *                   soon and stays staff-only on its own stage.
 *
 * `legacy` says where an unmoved entry goes:
 *   { modal: "<tab id>" }  -> opens FTSWorkspaceModal on that tab
 *   { route: "<name>" }    -> pushes that existing route
 *
 * `stage` gates ONE section, inside the area's own stage (settingsStage.js).
 * Without it a section follows the area. With it, the area can move down the
 * ladder while the parts that are not finished stay staff-only -- which is what
 * lets the old modal be retired a tab at a time instead of all at once.
 *
 * `access` is the least role a section asks for in THIS workspace (D-12,
 * settingsAccess.js): "member" or "userAdmin". Without it a workspace section
 * is for workspace admins; the "Your account" group is everyone's.
 */
import { ReleaseStage } from "../commons/enum";
export const SETTINGS_GROUPS = [
  {
    id: "workspace",
    name: "Workspace",
    items: [
      // Everyone in the workspace: a member gets the read-only summary (state 7).
      { id: "overview", name: "Overview", icon: "table-cells-large", moved: true, access: "member" },
      { id: "general", name: "General", icon: "cog", moved: true },
      // Today's people page, embedded: it went live with the area (2026-09-30), and /users now
      // redirects here.
      { id: "people", name: "People & seats", icon: "user-friends", moved: true, access: "userAdmin" },
      { id: "gney", name: "GNey", icon: "wand-magic-sparkles", moved: true },
      // Pages Inistate publishes for this workspace on e.inistate.com, opened from the side menu and
      // the mobile app (docs/integration-portal-link-proposal.md §4.3, PL-1 in the SVN repo).
      // Staff-only at first, then the usual ladder (W-25).
      { id: "portals", name: "Portals", icon: "desktop", moved: true, stage: ReleaseStage.ALPHA },
    ],
  },
  {
    id: "billing",
    name: "Plan & billing",
    items: [
      { id: "plan", name: "Plan", icon: "shopping-bag", moved: true },
      { id: "usage", name: "Usage", icon: "chart-bar", moved: true },
      { id: "credits", name: "AI credits", icon: "bolt", moved: true },
      { id: "billing", name: "Billing & invoices", icon: "file-alt", moved: true },
    ],
  },
  {
    id: "access",
    name: "Access",
    items: [
      { id: "security", name: "Security", icon: "lock", moved: true },
      // Who has programmatic access to THIS workspace's data (proposal §4 #14, unbuilt). Not the
      // same thing as the person's own tokens, which live under "Your account" below: a
      // connection is owned by a person and can span workspaces, so revoking one here can only
      // ever narrow its grant to this workspace.
      { id: "app-access", name: "App access", icon: "plug", moved: false, comingSoon: true, internalOnly: true, stage: ReleaseStage.ALPHA },
      { id: "audit", name: "Audit log", icon: "list-ul", moved: false, comingSoon: true, stage: ReleaseStage.ALPHA },
    ],
  },
  {
    id: "data",
    name: "Data",
    items: [
      // docs/integration-data-sources-ux-proposal.md §3.1-3.2 (SVN): AutoCount's guided setup, then
      // the plain list of every source. AutoCount went live on 2026-10-14 (KY: "live, instead of
      // alpha"), so it follows the area; the plain list stays staff-only with the rest of the group.
      { id: "autocount", name: "AutoCount", icon: "plug", moved: true },
      { id: "data-sources", name: "Data sources", icon: "server", moved: true, stage: ReleaseStage.ALPHA },
      { id: "data", name: "Export & retention", icon: "server", moved: false, comingSoon: true, stage: ReleaseStage.ALPHA },
    ],
  },
  {
    // The person's own settings. `scope: "account"` is what tells the frame these pages do NOT
    // belong to the workspace named at the top of the sidebar -- change your name here and it
    // changes everywhere you work.
    id: "account",
    name: "Your account",
    scope: "account",
    items: [
      { id: "profile", name: "Profile", icon: "user", moved: true },
      { id: "password", name: "Password", icon: "key", moved: true },
      { id: "appearance", name: "Appearance", icon: "moon", moved: true },
      { id: "connections", name: "Connections", icon: "plug", moved: true },
    ],
  },
];

/** Flat list, in sidebar order. */
export function settingsItems() {
  return SETTINGS_GROUPS.reduce((all, g) => all.concat(g.items), []);
}

export function settingsItem(id) {
  return settingsItems().find(x => x.id === id) || null;
}

/** The group a section belongs to, or null. */
export function settingsGroupOf(id) {
  return SETTINGS_GROUPS.find(g => g.items.some(x => x.id === id)) || null;
}

/** True for the person's own settings rather than the workspace's. */
export function isAccountSection(id) {
  const group = settingsGroupOf(id);
  return !!group && group.scope === "account";
}

/** Section ids that have a page here — the router's whitelist. */
export function movedSections() {
  return settingsItems().filter(x => x.moved).map(x => x.id);
}

/**
 * The old routes that folded into the frame at GA (W-3, phase P4), and the page each one opens
 * now. The Lite twin's people routes (/lite/users) are NOT here yet: its page is a different
 * component with its own behaviour and tests, and W-18 retires it as a piece of its own.
 */
export const FOLDED_ROUTES = Object.freeze({
  users: { section: "people" },
  "users-lobby": { section: "people", tab: "lobby" },
  "users-inactive": { section: "people", tab: "inactive" },
  "users-invitation": { section: "people", tab: "invitation" },
  connections: { section: "connections" },
});

/** The settings location an old route opens now, or null for a route that did not fold in. */
export function foldedLocation(routeName) {
  const fold = FOLDED_ROUTES[routeName];
  if (!fold) return null;
  return {
    name: "settings",
    params: { section: fold.section },
    query: fold.tab ? { tab: fold.tab } : {},
  };
}

/**
 * The page a shell opens for the old dialog's options: `settingsSection` when the caller names a
 * page the dialog has no tab of its own for (a billing email's credits or invoices), else the
 * dialog's tab mapped to its new home, else the Overview. The page itself still falls back to
 * the Overview for anyone who cannot reach the one asked for.
 */
export function settingsSectionFor(options) {
  const opts = options || {};
  if (opts.settingsSection) return opts.settingsSection;
  if (opts.section === "subscription") return "plan";
  if (opts.section === "access") return "security";
  return "overview";
}

export default { SETTINGS_GROUPS, FOLDED_ROUTES, settingsItems, settingsItem, settingsGroupOf, isAccountSection, movedSections, foldedLocation, settingsSectionFor };
